import { isEmpty, isEqual, isEqualWith } from 'lodash';
import { Fragment, Mark, Node } from 'prosemirror-model';

export function attrsEq(attrsA: Dictionary, attrsB: Dictionary, customizer?: (valueA: any, valueB: any, name: string) => boolean): boolean {
  return isEqualWith(attrsA, attrsB, customizer);
}

export function marksEq(nodeA: Node, nodeB: Node, customizer?: (valueA: any, valueB: any, name: string) => boolean): boolean {
  return isEqualWith(nodeA.marks, nodeB.marks, customizer);
}

export function fragmentEq(fragA: Fragment, fragB: Fragment): boolean {
  if (fragA.childCount !== fragB.childCount) {
    return false;
  }

  for (let i = 0; i < fragA.childCount; i += 1) {
    if (!nodeEq(fragA.child(i), fragB.child(i))) {
      return false;
    }
  }

  return true;
}

/**
 * Compares two nodes to see if they are equal. Uses NodeSpec.eq when it exists.
 * @param nodeA One of the nodes to compare.
 * @param nodeB The other node to compare.
 * @returns Returns true if the nodes are the same.
 */
export function nodeEq(nodeA: Node, nodeB: Node): boolean {
  if (typeof nodeA.type.spec.eq === 'function') {
    return nodeA.type.spec.eq(nodeA, nodeB);
  } else {
    if (nodeA === nodeB) {
      return true;
    }

    if (!nodeA.sameMarkup(nodeB)) {
      return false;
    }

    if (!fragmentEq(nodeA.content, nodeB.content)) {
      return false;
    }

    if (nodeA.isText && nodeB.isText && nodeA.text !== nodeB.text) {
      return false;
    }

    return true;
  }
}

/**
 * Returns true if the node's attributes are the same as the given attributes. Otherwise returns false.
 * Returns false if the node is null or undefined.
 * @param node The node to compare the attributes with.
 * @param attrs The attributes to compare with the node.
 */
export function hasNodeAttrs(node: Node, attrs: Dictionary): boolean {
  if (!node) {
    return false;
  }

  const nodeAttrs = !node.attrs || isEmpty(node.attrs) ? null : node.attrs;
  attrs = !attrs || isEmpty(attrs) ? null : attrs;

  return isEqual(nodeAttrs, attrs);
}

export interface NodeSpecEq {
  attrs?: (nodeA: Node, nodeB: Node) => boolean;
  content?: (nodeA: Node, nodeB: Node) => boolean;
  marks?: (nodeA: Node, nodeB: Node) => boolean;
  text?: (nodeA: Node, nodeB: Node) => boolean;
  type?: (nodeA: Node, nodeB: Node) => boolean;
}

/**
 * Creates a function to be used by nodes that override their eq function.
 * Only the custom parts of node comparison need to be passed in via the options parameter.
 * Any options left undefined use the default behavior to compare nodes.
 * For example, to only override how a node's attrs are compared then only the attrs options needs to be defined.
 * @param options
 * @returns Returns a function that takes two nodes as parameters and returns a boolean.
 */
export function nodeSpecEq(options: NodeSpecEq) {
  return (nodeA: Node, nodeB: Node): boolean => {
    // Check instance equality
    if (nodeA === nodeB) {
      return true;
    }

    // Check type equality
    if (typeof options.type === 'function') {
      if (!options.type(nodeA, nodeB)) {
        console.log('custom type mismatch', options.type);
        return false;
      }
    } else {
      if (nodeA.type !== nodeB.type) {
        console.log('default type mismatch');
        return false;
      }
    }

    // Check attrs equality
    if (typeof options.attrs === 'function') {
      if (!options.attrs(nodeA, nodeB)) {
        return false;
      }
    } else {
      if (!attrsEq(nodeA.attrs, nodeB.attrs ?? nodeB.type.defaultAttrs ?? {})) {
        return false;
      }
    }

    // Check marks equality
    if (typeof options.marks === 'function') {
      if (!options.marks(nodeA, nodeB)) {
        return false;
      }
    } else {
      if (!Mark.sameSet(nodeA.marks, nodeB.marks ?? Mark.none)) {
        return false;
      }
    }

    // Check content equality
    if (typeof options.content === 'function') {
      if (!options.content(nodeA, nodeB)) {
        return false;
      }
    } else {
      if (!fragmentEq(nodeA.content, nodeB.content)) {
        return false;
      }
    }

    // Check text equality
    if (typeof options.text === 'function') {
      if (!options.text(nodeA, nodeB)) {
        return false;
      }
    } else {
      if (nodeA.isText && nodeB.isText && nodeA.text !== nodeB.text) {
        return false;
      }
    }

    return true;
  };

}
